import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../database/config';

const navLinks = [
  { to: '/about', label: 'About Us' },
  { to: '/contact', label: 'Contact Us' },
  { to: '/blog', label: 'Blog' },
  { to: '/locations', label: 'Locations' },
  { to: '/faqs', label: 'FAQs' },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyName, setCompanyName] = useState(() => {
    try {
      const cached = localStorage.getItem('bento_navbar_data');
      return cached ? JSON.parse(cached).companyName : '';
    } catch { return ''; }
  });
  const [logoUrl, setLogoUrl] = useState(() => {
    try {
      const cached = localStorage.getItem('bento_navbar_data');
      return cached ? JSON.parse(cached).logoUrl : '';
    } catch { return ''; }
  });
  const [joinUsText, setJoinUsText] = useState(() => {
    try {
      const cached = localStorage.getItem('bento_navbar_data');
      return cached ? JSON.parse(cached).joinUsText : '';
    } catch { return ''; }
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isBulkOrderPage = location.pathname === '/bulk-order';

  // Handle Navbar Background on Scroll
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Handle Body Scroll Lock when Mobile Menu is Open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const fetchNavBarData = async () => {
      try {
        const [companyNameSnap, logoSnap, joinUsSnap] = await Promise.all([
          getDoc(doc(db, 'home', 'navBar', 'companyName', 'companyName')),
          getDoc(doc(db, 'home', 'navBar', 'imgLogo', 'imgLogo')),
          getDoc(doc(db, 'home', 'navBar', 'joinsUs', 'joinsUs'))
        ]);

        let newCompany = companyName;
        let newLogo = logoUrl;
        let newJoin = joinUsText;

        if (companyNameSnap.exists()) {
          newCompany = companyNameSnap.data().text || 'BentoBox';
          setCompanyName(newCompany);
        }

        if (logoSnap.exists()) {
          newLogo = logoSnap.data().imgLogo || '';
          setLogoUrl(newLogo);
        }

        if (joinUsSnap.exists()) {
          newJoin = joinUsSnap.data().text || 'Bulk Order';
          setJoinUsText(newJoin);
        }

        localStorage.setItem('bento_navbar_data', JSON.stringify({
          companyName: newCompany,
          logoUrl: newLogo,
          joinUsText: newJoin
        }));
      } catch (error) {
        console.error('Error fetching NavBar data:', error);
      }
    };
    fetchNavBarData();
  }, []);

  const handleHowItWorks = (e) => {
    if (isHomePage) {
      e.preventDefault();
      document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  // NEW: Mouse movement handler for the interactive border glow
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
  };

  return (
    <>
      <style>{`
        :root {
          --nav-transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          --primary-orange: #F97316;
          --primary-orange-hover: #EA580C;
        }

        .vg-navbar {
          font-family: 'Sora', sans-serif;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 24px;
          
          border-bottom: 1px solid transparent;
          background: transparent;
          
          transition: var(--nav-transition);
          box-shadow: none;
        }

        .vg-navbar.scrolled {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(24px) saturate(150%);
          -webkit-backdrop-filter: blur(24px) saturate(150%);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }

        .vg-navbar.light-page {
          background: transparent;
          border-bottom: 1px solid transparent;
          box-shadow: none;
        }
        
        .vg-navbar.scrolled .vg-brand-name,
        .vg-navbar.light-page .vg-brand-name { color: #0F172A; }
        
        .vg-navbar.scrolled .vg-nav-link,
        .vg-navbar.light-page .vg-nav-link { color: #64748B; }
        
        .vg-navbar.scrolled .vg-nav-link:hover,
        .vg-navbar.light-page .vg-nav-link:hover { color: #0F172A; background: rgba(15, 23, 42, 0.06); }
        
        .vg-navbar.scrolled .vg-hamburger span,
        .vg-navbar.light-page .vg-hamburger span { background: #0F172A; }
        
        .vg-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          flex-shrink: 0;
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .vg-brand:hover { transform: scale(1.04); }
        
        .vg-brand-icon {
          width: 38px;
          height: 38px;
          border-radius: 32px;
          overflow: hidden;
          flex-shrink: 0;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: var(--nav-transition);
        }
        .vg-navbar.scrolled .vg-brand-icon,
        .vg-navbar.light-page .vg-brand-icon { background: #F1F5F9; border: 1px solid rgba(0,0,0,0.05); }
        .vg-brand-icon img { width: 100%; height: 100%; object-fit: cover; }
        .vg-brand-name {
          font-size: 18px;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: -0.5px;
          white-space: nowrap;
        }

        .vg-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex: 1;
          justify-content: center;
        }
        .vg-nav-link {
          font-family: 'Sora', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.7);
          text-decoration: none;
          padding: 8px 16px;
          border-radius: 9999px;
          white-space: nowrap;
          transition: all 0.25s ease;
        }
        .vg-nav-link:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.12);
          transform: translateY(-1px);
        }
        
        .vg-nav-link.active,
        .vg-mobile-link.active {
          color: var(--primary-orange) !important;
        }

        /* Base Button Styles */
        .vg-cta-static, .vg-cta, .vg-mobile-cta, .vg-mobile-cta-static {
          position: relative; /* Essential for border glow positioning */
          overflow: hidden;   /* Keeps the glow contained to the border radius */
        }

        .vg-cta-static {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 24px;
          border-radius: 9999px;
          background: linear-gradient(135deg, var(--primary-orange), var(--primary-orange-hover));
          box-shadow: 0 8px 20px -6px rgba(249,115,22,0.6), inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -3px 0 0 #ffffff;
          border: none;
          color: #ffffff;
          font-family: 'Sora', sans-serif;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
          text-decoration: none;
          letter-spacing: 0.2px;
          pointer-events: none;
        }

        .vg-cta {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 24px;
          border-radius: 9999px;
          background: linear-gradient(135deg, var(--primary-orange), var(--primary-orange-hover));
          box-shadow: 0 8px 20px -6px rgba(249,115,22,0.6), inset 0 1px 1px rgba(255,255,255,0.3);
          border: none;
          color: #ffffff;
          font-family: 'Sora', sans-serif;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
          text-decoration: none;
          letter-spacing: 0.2px;
          transition: var(--nav-transition);
        }

        /* Old Sweep Shine Animation - kept on ::after */
        .vg-cta::after {
          content: '';
          position: absolute;
          top: 0; left: -100%; width: 50%; height: 100%;
          background: linear-gradient(to right, transparent, rgba(255,255,255,0.3), transparent);
          transform: skewX(-20deg);
          transition: all 0.5s ease;
          z-index: 1;
        }
        .vg-cta:hover {
          box-shadow: 0 12px 24px -8px rgba(249,115,22,0.8);
          transform: translateY(-2px);
        }
        .vg-cta:hover::after {
          left: 150%;
        }
        .vg-cta:active { transform: translateY(0); }

        /* --- ✨ PREMIUM CURSOR BORDER GLOW ✨ --- */
        .vg-cta::before, 
        .vg-cta-static::before,
        .vg-mobile-cta::before,
        .vg-mobile-cta-static::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 2px; /* Increased from 1.5px for a more visible border track */
          
          /* Wider 100px radius and much stronger color stops so it stands out against the orange button */
          background: radial-gradient(
            100px circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
            rgba(255, 255, 255, 1) 0%,        /* Pure white hot core */
            rgba(255, 210, 240, 0.95) 30%,    /* Bright pinkish-white halo */
            rgba(249, 115, 22, 0.85) 65%,     /* Strong orange edge */
            transparent 100%
          );
          
          /* The magic trick to cut out the inside, leaving only the edge */
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          
          opacity: 0;
          transition: opacity 0.3s ease; /* Faster fade-in so it reacts instantly */
          pointer-events: none;
          z-index: 2; 
        }

        .vg-cta:hover::before,
        .vg-cta-static:hover::before,
        .vg-mobile-cta:hover::before,
        .vg-mobile-cta-static:hover::before {
          opacity: 1;
        }

        .vg-hamburger {
          display: none;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 40px;
          height: 40px;
          background: transparent;
          border: none;
          cursor: pointer;
          flex-shrink: 0;
          position: relative;
        }
        .vg-hamburger span {
          display: block;
          position: absolute;
          height: 2px;
          background: #ffffff;
          border-radius: 4px;
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        
        .vg-hamburger span:nth-child(1) { width: 20px; transform: translateY(-7px); }
        .vg-hamburger span:nth-child(2) { width: 20px; transform: translateY(0); border-radius: 4px; }
        .vg-hamburger span:nth-child(3) { width: 20px; transform: translateY(7px); }

        .vg-hamburger:hover { opacity: 0.8; }

        .vg-hamburger.open span:nth-child(1) { 
          transform: translateY(0) rotate(45deg); 
          width: 22px;
        }
        .vg-hamburger.open span:nth-child(2) { 
          opacity: 0; 
          transform: scaleX(0); 
        }
        .vg-hamburger.open span:nth-child(3) { 
          transform: translateY(0) rotate(-45deg); 
          width: 22px; 
        }

        .vg-mobile-menu {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          z-index: 49; 
          
          background: rgba(15, 23, 42, 0.98);
          
          @supports (backdrop-filter: blur(24px)) {
            background: rgba(15, 23, 42, 0.85);
            backdrop-filter: blur(24px) saturate(180%);
            -webkit-backdrop-filter: blur(24px) saturate(180%);
          }
          
          padding: 100px 24px 40px 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .vg-mobile-menu.open {
          opacity: 1;
          visibility: visible;
          pointer-events: all;
        }

        .vg-mobile-link {
          font-family: 'Sora', sans-serif;
          font-size: 20px; 
          font-weight: 600;
          color: rgba(255,255,255,0.7);
          text-decoration: none;
          padding: 12px 24px;
          border-radius: 16px;
          transition: all 0.2s ease;
          width: 100%;
          text-align: center;
          max-width: 300px;
        }
        .vg-mobile-link:hover { 
          background: rgba(255,255,255,0.08); 
          color: #ffffff; 
          transform: translateY(-2px);
        }

        .vg-mobile-cta, .vg-mobile-cta-static {
          margin-top: 24px;
          text-align: center;
          font-family: 'Sora', sans-serif;
          font-size: 16px;
          font-weight: 700;
          padding: 16px;
          border-radius: 9999px;
          text-decoration: none;
          display: block;
          width: 100%;
          max-width: 300px;
          transition: var(--nav-transition);
        }
        .vg-mobile-cta {
          background: linear-gradient(135deg, var(--primary-orange), var(--primary-orange-hover));
          box-shadow: 0 8px 24px rgba(249,115,22,0.3);
          color: #ffffff;
        }
        .vg-mobile-cta-static {
          background: rgba(255,255,255,0.1);
          color: var(--primary-orange);
          border: 1px solid rgba(249,115,22,0.5);
          pointer-events: none;
        }
        .vg-mobile-cta:active { transform: scale(0.98); }

        @media (max-width: 900px) {
          .vg-nav { display: none; }
          .vg-cta, .vg-cta-static { display: none; }
          .vg-hamburger { display: flex; }
          .vg-navbar {
            padding: 10px 16px;
          }
          .vg-brand-icon {
            width: 32px;
            height: 32px;
          }
          .vg-brand-name {
            font-size: 15px;
          }
        }
      `}</style>

      <header className={`vg-navbar ${isScrolled ? 'scrolled' : ''} ${!isHomePage && !isScrolled ? 'light-page' : ''}`}>
        {/* Brand */}
        <Link to="/" className="vg-brand" onClick={() => setMobileMenuOpen(false)}>
          {logoUrl && (
            <div className="vg-brand-icon">
              <img
                src={logoUrl}
                alt={`${companyName} Logo`}
                width={36}
                height={36}
                fetchpriority="high"
                decoding="async"
              />
            </div>
          )}
          <span className="vg-brand-name">{companyName}</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="vg-nav">
          <Link to="/about" className={`vg-nav-link ${location.pathname === '/about' ? 'active' : ''}`}>About Us</Link>
          <Link
            to="/"
            state={{ scrollTo: 'how-it-works' }}
            onClick={handleHowItWorks}
            className="vg-nav-link"
          >
            How it works
          </Link>
          {navLinks.filter(l => l.to !== '/about').map((link) => (
            <Link key={link.to} to={link.to} className={`vg-nav-link ${location.pathname === link.to ? 'active' : ''}`}>
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Bulk Order CTA — Desktop */}
        <Link
          to="/bulk-order"
          className={isBulkOrderPage ? 'vg-cta-static' : 'vg-cta'}
          onMouseMove={handleMouseMove}
        >
          {joinUsText}
        </Link>

        {/* Hamburger — Mobile */}
        <button
          className={`vg-hamburger ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <span /><span /><span />
        </button>
      </header>

      {/* Mobile Dropdown - Full Screen */}
      <div className={`vg-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <Link to="/about" className={`vg-mobile-link ${location.pathname === '/about' ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>About Us</Link>
        <Link
          to="/"
          state={{ scrollTo: 'how-it-works' }}
          onClick={handleHowItWorks}
          className="vg-mobile-link"
        >
          How it works
        </Link>
        {navLinks.filter(l => l.to !== '/about').map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`vg-mobile-link ${location.pathname === link.to ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {link.label}
          </Link>
        ))}
        {/* Bulk Order CTA — Mobile */}
        <Link
          to="/bulk-order"
          className={isBulkOrderPage ? 'vg-mobile-cta-static' : 'vg-mobile-cta'}
          onClick={() => setMobileMenuOpen(false)}
          onMouseMove={handleMouseMove}
        >
          {joinUsText}
        </Link>
      </div>
    </>
  );
};

export default Navbar;